import React, { useCallback, useMemo, useState } from 'react'
import { APIProvider, Map, useApiIsLoaded } from '@vis.gl/react-google-maps'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { MapMessage } from './messages'
import useWindowMessage from 'src/shared/js/hooks/useWindowMessage'
import config from 'config'
import DetailMarker from './DetailMarker'
import FulfillmentMarker from './FulfillmentMarker'
import {
  DiningOptionBehavior,
  useRestaurantLocationsQuery,
  RestaurantLocationsQuery
} from 'src/apollo/onlineOrdering'
import { getLocale } from '@toasttab/buffet-pui-locale-utilities'
import { LoadingSpinner } from 'src/shared/components/common/loading_spinner/LoadingSpinner'

export type MarkerStyle = 'default' | 'locationDetails'

type OORestaurant = NonNullable<
  NonNullable<RestaurantLocationsQuery['restaurants']>[0]
> & { __typename: 'Restaurant' }

type Props = {
  sourceId?: string | null
  useOrderFulfillment?: boolean
  locationGuids: string[]
  selectedGuid?: string | null
  color?: string
  targetOrigin: string
  selectedDiningOption?: DiningOptionBehavior
  markerStyle?: string | null
  orderPathPattern: string | null
  hostName: string | null
}

const GoogleMap = (props: Props) => {
  const client = useOOClient()
  const { data: locationsData } = useRestaurantLocationsQuery({
    variables: { restaurantGuids: props.locationGuids },
    client
  })
  const [openGuid, setOpenGuid] = useState<string>()
  const isLoaded = useApiIsLoaded()

  useWindowMessage({
    origin: props.targetOrigin,
    sourceId: props.sourceId,
    messageHandlers: {
      [MapMessage.CLOSED]: () => setOpenGuid(undefined),
      [MapMessage.SELECTED_GUID]: (data: { guid: string }) =>
        setOpenGuid(data.guid)
    }
  })

  const interact = useCallback(() => {
    setOpenGuid(undefined)
    window.parent.postMessage(
      JSON.stringify({
        name: MapMessage.INTERACT,
        data: { sourceId: props.sourceId }
      }),
      props.targetOrigin
    )
  }, [props])

  const closeInfoWindow = useCallback(() => {
    setOpenGuid(undefined)
  }, [setOpenGuid])

  const updateOpenGuid = useCallback(
    (guid: string) => {
      setOpenGuid(guid)
      window.parent.postMessage(
        JSON.stringify({
          name: MapMessage.SELECTED_PIN,
          data: {
            sourceId: props.sourceId,
            guid
          }
        }),
        props.targetOrigin
      )
    },
    [props]
  )

  const updateSelectedData = useCallback(
    (guid: string, diningOptionBehavior?: DiningOptionBehavior) => {
      setOpenGuid(undefined)
      window.parent.postMessage(
        JSON.stringify({
          name: MapMessage.SELECTED_GUID,
          data: {
            guid,
            diningOptionBehavior,
            sourceId: props.sourceId
          }
        }),
        props.targetOrigin
      )
    },
    [props]
  )

  // Calculate bounds from all restaurant locations
  const { defaultBounds, defaultCenter } = useMemo(() => {
    if (!locationsData?.restaurants?.length) {
      return { defaultBounds: undefined, defaultCenter: undefined }
    }

    const locations = locationsData.restaurants
      .filter(
        (rx) =>
          rx?.__typename === 'Restaurant' &&
          rx?.location?.latitude &&
          rx?.location?.longitude
      )
      .map((rx: OORestaurant) => ({
        lat: rx.location!.latitude!,
        lng: rx.location!.longitude!
      }))

    if (locations.length === 0) {
      return { defaultBounds: undefined, defaultCenter: undefined }
    }

    // Single location - return center only
    if (locations.length === 1) {
      return { defaultBounds: undefined, defaultCenter: locations[0] }
    }

    // Multiple locations - calculate bounds
    const lats = locations.map((loc) => loc.lat)
    const lngs = locations.map((loc) => loc.lng)

    return {
      defaultBounds: {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs),
        padding: 50
      },
      defaultCenter: undefined
    }
  }, [locationsData])

  if (!locationsData?.restaurants?.length) {
    return null
  }

  const markers = locationsData?.restaurants?.map((rx: OORestaurant) => {
    if (
      rx?.__typename === 'Restaurant' &&
      rx?.location?.latitude &&
      rx?.location?.longitude
    ) {
      return props.markerStyle === 'locationDetails' ||
        !rx.onlineOrderingEnabled ||
        !rx.shortUrl ? (
        <DetailMarker
          onClick={() => updateOpenGuid(rx.guid)}
          onClose={closeInfoWindow}
          isSelected={props.selectedGuid === rx.guid}
          isInfoOpen={openGuid === rx.guid}
          position={{ lat: rx.location.latitude, lng: rx.location.longitude }}
          key={rx.guid}
          restaurant={rx as OORestaurant}
          selectedDiningOption={props.selectedDiningOption}
        />
      ) : (
        <FulfillmentMarker
          orderPathPattern={props.orderPathPattern}
          shortUrl={rx.shortUrl}
          hostName={props.hostName}
          useOrderFulfillment={props.useOrderFulfillment}
          onClick={() => updateOpenGuid(rx.guid)}
          onClose={closeInfoWindow}
          onSelect={(guid, diningOptionBehavior) =>
            updateSelectedData(guid, diningOptionBehavior)
          }
          isInfoOpen={openGuid === rx.guid}
          key={rx.guid}
          position={{ lat: rx.location.latitude, lng: rx.location.longitude }}
          isSelected={props.selectedGuid === rx.guid}
          selectedDiningOption={props.selectedDiningOption}
          restaurant={rx as OORestaurant}
        />
      )
    }

    return null
  })

  return (
    <>
      {isLoaded ? (
        <Map
          style={{
            width: '100%',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.9)'
          }}
          onClick={interact}
          onDragstart={interact}
          defaultZoom={defaultCenter ? 8 : undefined}
          defaultCenter={defaultCenter}
          defaultBounds={defaultBounds}
          disableDefaultUI={true}
          mapId={config.gMapsId}
        >
          {markers}
        </Map>
      ) : (
        <LoadingSpinner color={props.color} />
      )}
    </>
  )
}

const WrappedMap = (props: Props) => (
  <APIProvider apiKey={config.gMapsAPIKey} language={getLocale()}>
    <GoogleMap {...props} />
  </APIProvider>
)
export default WrappedMap
